import React from "react";
import { useIntl } from "react-intl";
import { resourceTitle } from "../../../utils/format";
import { commonMessages } from "../../../utils/messages";
import capitalize from "lodash/capitalize";
import ResourceMiniature from "../ResourceMiniature";
import { ResourceMiniatureProps } from "../types";
import { useAllSubjectsData } from "../../../hooks/useData";
import {
    LearningSet,
    LearningSetTypes,
} from "../../../interfaces/AthenaResources";
import MiniLoader from "../../../components/Loader/MiniLoader";
import { useCollectionItemQuery } from "../../../hooks/queries/useCollectionsQueries";
import {
    CollectionResourcePath,
    ModuleResourcePath,
    ResourcePath,
} from "../../../pages/Workmodes/Playlist/Teacher/utils";

type Props = Pick<
    ResourceMiniatureProps,
    "onClick" | "draggable" | "className" | "active"
> & {
    path: ResourcePath;
};

const TeacherResourceMiniature = ({
    path,
    ...resourceMiniatureProps
}: Props) => {
    const resourceInfo = useResourceInfo(path);

    return (
        <ResourceMiniature {...resourceMiniatureProps}>
            {resourceInfo ? (
                <>
                    <h4>{resourceInfo.title}</h4>
                    {resourceInfo.description && (
                        <p>{resourceInfo.description}</p>
                    )}
                </>
            ) : (
                <MiniLoader />
            )}
        </ResourceMiniature>
    );
};
export default TeacherResourceMiniature;

const useResourceInfo = (path: ResourcePath) => {
    const intl = useIntl();

    const data = useAllSubjectsData();
    const setId =
        path instanceof CollectionResourcePath ? path.setId : undefined;
    const setQuery = useCollectionItemQuery(
        path instanceof CollectionResourcePath
            ? path.type
            : LearningSetTypes.Workshop,
        setId
    );

    if (path instanceof CollectionResourcePath) {
        if (setQuery.data) {
            const set = setQuery.data[setId!] as LearningSet;
            return { title: set.name };
        }
        return undefined;
    }

    if (path instanceof ModuleResourcePath) {
        const { moduleId, objectiveId, activityId, exerciseId } = path.MOAE;
        const shortTitles = resourceTitle(
            {
                module: { id: moduleId, short: true, numbering: true },
                objective: { id: objectiveId, short: true, numbering: true },
                activity: { id: activityId, short: true, numbering: true },
                exercise: { id: exerciseId, numbering: true },
            },
            data,
            intl
        );
        const longTitles = resourceTitle(
            {
                module: { id: moduleId },
                objective: { id: objectiveId },
                activity: { id: activityId },
            },
            data,
            intl
        );
        return {
            title: shortTitles
                ? `${shortTitles.module}. ${shortTitles.objective}. ${shortTitles.activity}. ${shortTitles.exercise}`
                : capitalize(intl.formatMessage(commonMessages.exercise)),
            description: `${longTitles.module} / ${longTitles.objective} /
                ${longTitles.activity}`,
        };
    }

    throw new Error("Unexpected resource path");
};
